import React from 'react'
import PropTypes from 'prop-types'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { formatISO } from 'date-fns'
import { IonButton } from '@ionic/react'
import { useSubmitMessages } from 'utils'
import {
  ADD_TECH_COMMENTS_ACTION,
  ADD_TECH_COMMENTS_LABEL,
  STALE_WORK_ORDER_MESSAGE,
  TECH_COMMENTS_SAVED_MESSAGE,
} from '../constants'
import { isStaleWorkOrderError } from '../specialActions'
import EtaResetFields from './EtaResetFields'
import SectionTextarea from './SectionTextarea'
import AddTechCommentsFields, {
  techCommentsValidation,
} from './AddTechCommentsFields'

const SPECIAL_ACTION_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'change_eta', label: 'Change ETA' },
  { value: ADD_TECH_COMMENTS_ACTION, label: ADD_TECH_COMMENTS_LABEL },
]
const RESCHEDULE_OR_DECLINE_OPTIONS = [
  { value: 'reschedule', label: 'Reschedule' },
  { value: 'decline', label: 'Decline' },
]

function getInitialValues() {
  return {
    special_action: 'none',
    change_eta_action: '',
    eta_date: formatISO(new Date(), { representation: 'date' }),
    eta_time: 'morning',
    reschedule_reason: '',
    reason_for_declining: '',
    tech_comments: '',
  }
}

const validationSchema = Yup.object({
  tech_comments: techCommentsValidation('special_action'),
  eta_date: Yup.string().when(['special_action', 'change_eta_action'], {
    is: (specialAction, changeEtaAction) =>
      specialAction === 'change_eta' && changeEtaAction === 'reschedule',
    then: (schema) => schema.required('Please enter your new ETA date'),
    otherwise: (schema) => schema,
  }),
  eta_time: Yup.string().when(['special_action', 'change_eta_action'], {
    is: (specialAction, changeEtaAction) =>
      specialAction === 'change_eta' && changeEtaAction === 'reschedule',
    then: (schema) => schema.required('Please enter your new ETA time'),
    otherwise: (schema) => schema,
  }),
  reschedule_reason: Yup.string().when(
    ['special_action', 'change_eta_action'],
    {
      is: (specialAction, changeEtaAction) =>
        specialAction === 'change_eta' && changeEtaAction === 'reschedule',
      then: (schema) => schema.required('Please provide a reschedule reason'),
      otherwise: (schema) => schema,
    }
  ),
  reason_for_declining: Yup.string().when(
    ['special_action', 'change_eta_action'],
    {
      is: (specialAction, changeEtaAction) =>
        specialAction === 'change_eta' && changeEtaAction === 'decline',
      then: (schema) => schema.required('Please provide a decline reason'),
      otherwise: (schema) => schema,
    }
  ),
})

function ReschedulingActionsForm({
  onDecline,
  onReschedule,
  onAddTechComments,
  onRefreshWorkOrder,
}) {
  const { displaySubmitSuccess, displaySubmitFailure } = useSubmitMessages()
  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        const resetToInitial = () => resetForm({ values: getInitialValues() })

        try {
          if (
            values.special_action === 'change_eta' &&
            values.change_eta_action === 'decline'
          ) {
            await onDecline({
              reason_for_declining: values.reason_for_declining,
            })
            displaySubmitSuccess('The work order was declined successfully.')
            resetToInitial()
            return
          }

          if (
            values.special_action === 'change_eta' &&
            values.change_eta_action === 'reschedule'
          ) {
            await onReschedule({
              eta_date: values.eta_date,
              eta_time: values.eta_time,
              reschedule_reason: values.reschedule_reason,
            })
            displaySubmitSuccess('The work order was rescheduled successfully.')
            resetToInitial()
            return
          }

          if (values.special_action === ADD_TECH_COMMENTS_ACTION) {
            await onAddTechComments({ tech_comments: values.tech_comments })
            displaySubmitSuccess(TECH_COMMENTS_SAVED_MESSAGE)
            resetToInitial()
          }
        } catch (error) {
          // The work order's status changed elsewhere, so the action is no
          // longer valid. Refetch so the right partial is offered again.
          if (isStaleWorkOrderError(error)) {
            displaySubmitFailure({
              customFormWideError: STALE_WORK_ORDER_MESSAGE,
            })
            resetToInitial()
            onRefreshWorkOrder()
            return
          }
          displaySubmitFailure(error?.errors, null, error)
        }
      }}
      validateOnBlur={false}
    >
      {({
        values,
        setFieldValue,
        setFieldTouched,
        handleChange,
        handleBlur,
        errors,
        touched,
        isSubmitting,
      }) => (
        <Form className="accept-decline-section">
          <h3 className="accept-decline-section__title">Special Actions</h3>
          <div className="accept-decline-section__field">
            <label
              className="accept-decline-section__label"
              htmlFor="rescheduling-special-actions"
            >
              Reschedule or Change Status
            </label>
            <select
              id="rescheduling-special-actions"
              name="special_action"
              className="accept-decline-section__input-native"
              value={values.special_action}
              onChange={(event) => {
                const nextValue = event.target.value
                setFieldValue('special_action', nextValue)
                if (nextValue !== 'change_eta') {
                  setFieldValue('change_eta_action', '')
                  setFieldValue('reschedule_reason', '')
                  setFieldValue('reason_for_declining', '')
                }
                if (nextValue !== ADD_TECH_COMMENTS_ACTION) {
                  setFieldValue('tech_comments', '')
                  setFieldTouched('tech_comments', false)
                }
              }}
            >
              {SPECIAL_ACTION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {values.special_action === 'change_eta' && (
            <div className="accept-decline-section__field">
              <label
                className="accept-decline-section__label"
                htmlFor="rescheduling-reschedule-or-decline"
              >
                reschedule or decline?
              </label>
              <select
                id="rescheduling-reschedule-or-decline"
                name="change_eta_action"
                className="accept-decline-section__input-native"
                value={values.change_eta_action}
                onChange={(event) => {
                  const nextValue = event.target.value
                  setFieldValue('change_eta_action', nextValue)
                  if (nextValue !== 'reschedule') {
                    setFieldValue('reschedule_reason', '')
                  }
                  if (nextValue !== 'decline') {
                    setFieldValue('reason_for_declining', '')
                  }
                }}
              >
                <option value="">Select an option</option>
                {RESCHEDULE_OR_DECLINE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {values.special_action === 'change_eta' &&
            values.change_eta_action === 'reschedule' && (
              <>
                <EtaResetFields
                  idPrefix="rescheduling-actions"
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                />

                <SectionTextarea
                  id="rescheduling-reschedule-reason"
                  name="reschedule_reason"
                  label="Reschedule Reason"
                  value={values.reschedule_reason}
                  error={errors.reschedule_reason}
                  touched={touched.reschedule_reason}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                />

                <IonButton
                  type="submit"
                  color="tss-grey-dark"
                  className="accept-decline-section__btn"
                >
                  Submit
                </IonButton>
              </>
            )}

          {values.special_action === 'change_eta' &&
            values.change_eta_action === 'decline' && (
              <>
                <SectionTextarea
                  id="rescheduling-decline-reason"
                  name="reason_for_declining"
                  label="Decline Reason"
                  value={values.reason_for_declining}
                  error={errors.reason_for_declining}
                  touched={touched.reason_for_declining}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                />

                <IonButton
                  type="submit"
                  color="tss-grey-dark"
                  className="accept-decline-section__btn"
                >
                  Submit
                </IonButton>
              </>
            )}

          {values.special_action === ADD_TECH_COMMENTS_ACTION && (
            <AddTechCommentsFields
              idPrefix="rescheduling-add"
              values={values}
              errors={errors}
              touched={touched}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              isSubmitting={isSubmitting}
            />
          )}
        </Form>
      )}
    </Formik>
  )
}

ReschedulingActionsForm.propTypes = {
  onDecline: PropTypes.func.isRequired,
  onReschedule: PropTypes.func.isRequired,
  onAddTechComments: PropTypes.func.isRequired,
  onRefreshWorkOrder: PropTypes.func.isRequired,
}

export default ReschedulingActionsForm
